<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <warehouse-write-off-dialog
      v-model="writeOffDialog"
      :edited-item="editedItem"
      @groceriesWrittenOff="addDocuments"
      @success="onSuccess"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['number']"
        >
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                @click="editItem(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                class="mr-2"
              />
              <remove-button @click="deleteItem(item)" />
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-delete-sweep-outline"
        :title="$t('no_write_offs')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_write_off') + ': ' + lastEntryDate
            : $t('no_write_offs_yet')
        "
      >
        <warehouse-button-trash @click="openWriteOffDialog" />
      </empty-state>

      <div class="fixed-bottom-button">
        <v-btn color="error" block @click="openWriteOffDialog">
          <v-icon class="mr-2">mdi-trash-can-outline</v-icon>
          {{ $t("warehouse.write-off-product") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        class="ordera-box-shadow striped-table"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
              {{ $t("warehouse.last_write_off") }}:
              <strong>{{ lastEntryDate }}</strong>
            </span>
            <v-spacer />
            <warehouse-button-trash @click="openWriteOffDialog" />
          </v-toolbar>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="editItem(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-delete-sweep-outline"
        :title="$t('no_write_offs')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_write_off') + ': ' + lastEntryDate
            : $t('no_write_offs_yet')
        "
      >
        <warehouse-button-trash @click="openWriteOffDialog" />
      </empty-state>
    </template>
  </div>
</template>

<script>
import { WarehouseButtonTrash } from "@/components/warehouse/buttons";
import WarehouseWriteOffDialog from "@/components/warehouse/dialogs/WarehouseWriteOffDialog.vue";
import { writeOffDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseDocumentTabMixin from "@/mixins/warehouse/warehouseDocumentTabMixin";
import warehouseService from "@/services/warehouse";

export default {
  components: {
    WarehouseButtonTrash,
    WarehouseWriteOffDialog,
    EmptyState,
    MobileDataCards,
  },
  mixins: [warehouseDocumentTabMixin],
  data: () => ({
    documentType: "write-off",
    writeOffDialog: false,
    editedItem: writeOffDocumentGrocery,
  }),

  created() {
    this.load();
  },

  methods: {
    onSuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    addDocuments(documents) {
      this.documents.push(...documents);
      this.showSuccessSnackbar(this.$t("write_off_documents_added"));
    },
    openWriteOffDialog() {
      this.editedItem = Object.assign(
        {},
        { ...writeOffDocumentGrocery, warehouse: this.filters.warehouse[0] }
      );
      this.writeOffDialog = true;
    },

    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.writeOffDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_write_off"),
          this.$t("warehouse.confirm_delete_write_off"),
          {
            color: "warning",
          }
        )
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.removeWarehouse(item.id);
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("write_off_document_deleted"));
        } catch (err) {
          this.showErrorSnackbar(this.$t("error_deleting_write_off_document"));
          console.warn(err);
        }
      }
    },
  },

  computed: {
    headers() {
      return [
        { text: "ID", value: "id", mobile: false },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 6 },
        { text: this.$t("warehouse.write-off_date"), value: "document_date", cols: 6 },
        { text: this.$t("note"), value: "notes", cols: 9 },
        { text: this.$t("status_created"), value: "created_at", mobile: false, cols: 9 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ];
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.write_off").toLowerCase(),
      ].join(" ");
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}
</style>
